// src/components/conciliacion/CuantificacionPrejudicialCard.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import html2pdf from "html2pdf.js";
import { actionObjetosGet } from "../../redux/actions/objetos/objetos";
import ScrollDateInput from "../calendar/ScrollDateInput";
import "./CuantificacionPrejudicialCard.css";

// ──────────────────────────────────────────
// SALARIOS MÍNIMOS HISTÓRICOS
// ──────────────────────────────────────────
const SMG = {
  2018: { general: 88.36,  frontera: 88.36  },
  2019: { general: 102.68, frontera: 176.72 },
  2020: { general: 123.22, frontera: 185.56 },
  2021: { general: 141.70, frontera: 213.39 },
  2022: { general: 172.87, frontera: 260.34 },
  2023: { general: 207.44, frontera: 312.41 },
  2024: { general: 248.93, frontera: 374.89 },
  2025: { general: 278.80, frontera: 419.88 },
  2026: { general: 278.80, frontera: 419.88 },
};

function obtenerSMG(anio, zona) {
  const keys = Object.keys(SMG).map(Number);
  const lo = Math.min(...keys), hi = Math.max(...keys);
  const y = anio < lo ? lo : anio > hi ? hi : anio;
  return SMG[y][zona];
}

// ──────────────────────────────────────────
// CÁLCULOS LABORALES (solo lo aplicable a Audiencia Prejudicial)
// ──────────────────────────────────────────
function calcDias(fi, fe) {
  const a = fi instanceof Date ? fi : new Date(String(fi).includes("T") ? fi : fi + "T00:00:00");
  const b = fe instanceof Date ? fe : new Date(String(fe).includes("T") ? fe : fe + "T00:00:00");
  return Math.floor((b - a) / 86400000) + 1;
}

function diasAnio(y) {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 366 : 365;
}

function diasVac(anios, anioEgreso) {
  const ley = anioEgreso >= 2023;
  const a = Math.floor(anios) || 1;
  if (!ley) {
    if (a === 1) return 6;  if (a === 2) return 8;  if (a === 3) return 10;
    if (a === 4) return 12; if (a <= 9) return 14;  if (a <= 14) return 16;
    if (a <= 19) return 18; if (a <= 24) return 20; if (a <= 29) return 22;
    return 24;
  }
  if (a === 1) return 12; if (a === 2) return 14; if (a === 3) return 16;
  if (a === 4) return 18; if (a === 5) return 20; if (a <= 10) return 22;
  if (a <= 15) return 24; if (a <= 20) return 26; if (a <= 25) return 28;
  if (a <= 30) return 30; return 32;
}

function calcSDI(sd, aniosS, anioEgreso) {
  const dv = diasVac(Math.floor(aniosS) || 1, anioEgreso);
  return sd * (1 + (15 + dv * 0.25) / 365);
}

function toDate(s) {
  if (s instanceof Date) return s;
  const str = String(s);
  return str.includes("T") ? new Date(str) : new Date(str + "T00:00:00");
}

function calcAguinaldo(sd, fi, fe) {
  const ing = toDate(fi), eg = toDate(fe);
  const ae = eg.getFullYear();
  const ini = ing.getFullYear() === ae ? ing : new Date(`${ae}-01-01T00:00:00`);
  const d = calcDias(ini, eg);
  const prop = (d / diasAnio(ae)) * 15;
  return { dias: prop, monto: prop * sd };
}

function calcVacaciones(sd, fi, fe) {
  const ing = toDate(fi), eg = toDate(fe);
  const tot = calcDias(ing, eg);
  const aniosS = tot / 365;
  const aniosC = Math.floor(aniosS);
  const aniversario = new Date(ing);
  aniversario.setFullYear(ing.getFullYear() + aniosC);
  const dUltimo = calcDias(aniversario, eg);
  const dvCorr = diasVac(aniosC + 1, eg.getFullYear());
  const prop = (dUltimo / 365) * dvCorr;
  return { diasTotales: prop, montoVac: prop * sd, montoPrima: prop * sd * 0.25 };
}

function calcPrima(aniosS, sd, anioEgreso, zona) {
  const smg = obtenerSMG(anioEgreso, zona);
  const tope = smg * 2;
  const base = Math.min(sd, tope);
  const dias = aniosS * 12;
  return { dias, salarioAplicado: base, topado: sd > tope, monto: dias * base };
}

function ejecutarCalculo(sd, sdiBase, fi, fe, zona, jornadaH, hs, sr, procede90, procedePrima) {
  if (!sd || sd <= 0 || !fi || !fe) return null;
  const ing = new Date(fi + "T00:00:00"), eg = new Date(fe + "T00:00:00");
  if (eg < ing) return null;
  const anioEg = eg.getFullYear();
  const diasT = calcDias(ing, eg);
  const aniosS = diasT / 365;
  const sdi = sdiBase > 0 ? sdiBase : calcSDI(sd, aniosS, anioEg);

  const montoInd  = procede90    ? sdi * 90 : 0;
  const prima     = procedePrima ? calcPrima(aniosS, sd, anioEg, zona)             : { dias: 0, monto: 0, salarioAplicado: 0, topado: false };
  const aguinaldo = calcAguinaldo(sd, fi, fe);
  const vacas     = calcVacaciones(sd, fi, fe);

  const horasD = Math.min(parseFloat(hs) || 0, 9);
  const horasT = Math.max(0, (parseFloat(hs) || 0) - 9);
  const salH   = sd / jornadaH;
  const semRec = parseFloat(sr) || 0;
  const montoHE = ((horasD * salH * 2) + (horasT * salH * 3)) * semRec;

  let total = aguinaldo.monto + vacas.montoVac + vacas.montoPrima;
  if (procede90)    total += montoInd;
  if (procedePrima) total += prima.monto;
  if (montoHE > 0)  total += montoHE;

  const aniosE = Math.floor(aniosS);
  const mesesE = Math.floor((diasT % 365) / 30);
  const diasE  = Math.floor((diasT % 365) % 30);

  return { sdi, montoInd, prima, aguinaldo, vacas, horasD, horasT, semRec, montoHE, total, aniosE, mesesE, diasE };
}

// ──────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────
const safeObj = (v) => (v && typeof v === "object" ? v : {});

const collapseSpaces = (s) => String(s ?? "").trim().replace(/\s+/g, " ");

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const coerceItemsFromSlice = (slice) => {
  if (!slice) return [];
  if (Array.isArray(slice.items)) return slice.items;
  if (Array.isArray(slice.data)) return slice.data;
  if (Array.isArray(slice?.data?.items)) return slice.data.items;
  if (Array.isArray(slice?.payload?.items)) return slice.payload.items;
  return [];
};

// Mapea el nombre libre del catálogo "Objeto de la solicitud" a la categoría
// legal interna que determina qué prestaciones proceden (mismo criterio que
// se usa en la cuantificación de Tribunal).
function inferCategoriaAccion(nombre) {
  const s = (nombre || "").toUpperCase();
  if (s.includes("REINSTALACION") || s.includes("REINSTALACIÓN")) return "despido_reinstalacion";
  if (s.includes("INDEMNIZACION") || s.includes("INDEMNIZACIÓN"))  return "despido_indemnizacion";
  if (s.includes("RESCISION")     || s.includes("RESCISIÓN"))      return "rescision_trabajador";
  if (s.includes("RENUNCIA"))                                       return "renuncia";
  if (s.includes("JUSTIFICADO")   || s.includes("JUSTIFICADA"))     return "despido_justificado";
  if (s.includes("TERMINACION")   || s.includes("TERMINACIÓN") || s.includes("VOLUNTARIA")) return "terminacion_voluntaria";
  return "terminacion_voluntaria";
}

const fmt = (v) => {
  const n = Number(v) || 0;
  return `$ ${n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtHoy = () => new Date().toLocaleDateString("es-MX");

const sanitizeFilename = (s) =>
  String(s || "")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .slice(0, 150);

// ──────────────────────────────────────────
// COMPONENT — Cuantificación de Audiencia Prejudicial
// ──────────────────────────────────────────
export default function CuantificacionPrejudicialCard({ data = null }) {
  const d = safeObj(data);
  const dispatch = useDispatch();

  const actorInicial   = toTitleCase(
    Array.isArray(d.trabajadores)
      ? d.trabajadores.map((t) => t?.nombre_trabajador).filter(Boolean).join(", ")
      : ""
  );
  const empresaInicial =
    (Array.isArray(d.razones_sociales) && d.razones_sociales.length
      ? d.razones_sociales.map((r) => r.razon_social).join(" / ")
      : d.nombre_empresa) || "";

  const hoy = new Date().toISOString().split("T")[0];

  // ── Catálogo "Objeto de la solicitud" ──
  useEffect(() => {
    dispatch(actionObjetosGet());
  }, [dispatch]);

  const objetosSlice = useSelector((state) => state.objetos || {});
  const objetosItems = useMemo(() => coerceItemsFromSlice(objetosSlice), [objetosSlice]);

  const objetosOptions = useMemo(() => {
    const opts = objetosItems
      .filter((o) => o && (o.active === undefined || o.active === null || Number(o.active) === 1))
      .map((o) => ({
        label: collapseSpaces(o?.nombre || ""),
        value: collapseSpaces(o?.nombre || ""),
      }))
      .filter((o) => o.label);

    const seen = new Set();
    return opts.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
  }, [objetosItems]);

  const objetoExpediente = collapseSpaces(d.nombre_objeto || d.tipo_objeto || d.objeto || "");

  // Si el objeto del expediente aún no aparece en el catálogo (p. ej. mientras carga), lo agregamos para no perderlo.
  const objetosOptionsFull = useMemo(() => {
    if (!objetoExpediente) return objetosOptions;
    const yaExiste = objetosOptions.some(
      (o) => o.value.toLowerCase() === objetoExpediente.toLowerCase()
    );
    return yaExiste ? objetosOptions : [{ label: objetoExpediente, value: objetoExpediente }, ...objetosOptions];
  }, [objetosOptions, objetoExpediente]);

  // ── Estado del formulario ──
  const [nombreTrabajador, setNombreTrabajador] = useState(actorInicial);
  const [nombreEmpresa,    setNombreEmpresa]    = useState(empresaInicial);
  const [tipoAccion,       setTipoAccion]       = useState(objetoExpediente);

  // Mantiene seleccionado el objeto real del expediente (o el primero del catálogo si no aplica).
  useEffect(() => {
    if (objetoExpediente) {
      setTipoAccion(objetoExpediente);
    } else if (!tipoAccion && objetosOptionsFull.length) {
      setTipoAccion(objetosOptionsFull[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objetoExpediente, objetosOptionsFull.length]);

  const categoriaAccion = inferCategoriaAccion(tipoAccion);

  const [fi, setFi] = useState("");
  const [fe, setFe] = useState("");

  const [salManual,    setSalManual]    = useState("");
  const [sdiManualOn,  setSdiManualOn]  = useState(false);
  const [sdiManualVal, setSdiManualVal] = useState("");

  const [zona, setZona] = useState("general");

  const [jornadaSel,  setJornadaSel]  = useState("8");
  const [horasSemana, setHorasSemana] = useState("");
  const [semanasRec,  setSemanasRec]  = useState("");

  const [calcAltOn,    setCalcAltOn]    = useState(false);
  const [salReclamado, setSalReclamado] = useState("");
  const [sdiReclamado, setSdiReclamado] = useState("");

  const effectiveSal     = parseFloat(salManual) || 0;
  const effectiveSdi     = sdiManualOn ? (parseFloat(sdiManualVal) || 0) : 0;
  const effectiveJornada = parseFloat(jornadaSel) || 8;

  // Reglas según acción seleccionada (audiencia prejudicial: sin 20 días ni salarios caídos)
  const procede90    = ["terminacion_voluntaria", "despido_indemnizacion", "despido_reinstalacion", "rescision_trabajador"].includes(categoriaAccion);
  const procedePrima = categoriaAccion !== "renuncia";
  const procedeNeg   = categoriaAccion === "terminacion_voluntaria";

  const calc = useMemo(() =>
    ejecutarCalculo(effectiveSal, effectiveSdi, fi, fe, zona, effectiveJornada, horasSemana, semanasRec, procede90, procedePrima),
    [effectiveSal, effectiveSdi, fi, fe, zona, effectiveJornada, horasSemana, semanasRec, procede90, procedePrima]
  );

  const calcAlt = useMemo(() => {
    if (!calcAltOn) return null;
    const salAlt = parseFloat(salReclamado) || 0;
    const sdiAlt = parseFloat(sdiReclamado) || 0;
    return ejecutarCalculo(salAlt, sdiAlt, fi, fe, zona, effectiveJornada, horasSemana, semanasRec, procede90, procedePrima);
  }, [calcAltOn, salReclamado, sdiReclamado, fi, fe, zona, effectiveJornada, horasSemana, semanasRec, procede90, procedePrima]);

  const mostrarHE = (parseFloat(horasSemana) > 0) && (parseFloat(semanasRec) > 0);
  const mostrarIndemnizaciones = procede90 || procedePrima;

  // ── Guardar / descargar el recibo como PDF (en Electron se guarda al equipo, en navegador se descarga) ──
  const receiptRef = useRef(null);
  const [guardandoPDF, setGuardandoPDF] = useState(false);

  const handleGuardarPDF = async () => {
    if (!receiptRef.current || guardandoPDF) return;

    const actor     = sanitizeFilename(nombreTrabajador) || "Solicitante";
    const demandado = sanitizeFilename(nombreEmpresa) || "Demandado";
    const filename  = `${actor} vs ${demandado}.pdf`;

    setGuardandoPDF(true);
    try {
      await html2pdf()
        .set({
          margin: 5,
          filename,
          html2canvas: {
            scale: 2,
            useCORS: true,
            ignoreElements: (el) => el.classList?.contains("cqp-btn-print"),
          },
          jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
        })
        .from(receiptRef.current)
        .save();
    } catch (err) {
      console.error("No se pudo generar el PDF de la cuantificación:", err);
    } finally {
      setGuardandoPDF(false);
    }
  };

  return (
    <div className="cqp-wrap">

      {/* ══════════════ LEFT: FORMULARIO ══════════════ */}
      <main className="cqp-left">

        {/* SECCIÓN 1 — Identificación y Acción */}
        <section className="cqp-section">
          <h2 className="cqp-section-h">1. Datos de Identificación y Acción</h2>
          <div className="cqp-grid">
            <div className="cqp-field">
              <label>Nombre del Trabajador (Actor)</label>
              <input type="text" placeholder="Ej. Juan Pérez"
                value={nombreTrabajador} onChange={(e) => setNombreTrabajador(e.target.value)} />
            </div>
            <div className="cqp-field">
              <label>Empresa / Patrón (Demandado)</label>
              <input type="text" placeholder="Ej. Empresa S.A. de C.V."
                value={nombreEmpresa} onChange={(e) => setNombreEmpresa(e.target.value)} />
            </div>
            <div className="cqp-field cqp-full">
              <label>Acción Demandada / Objeto de la Solicitud</label>
              <select value={tipoAccion} onChange={(e) => setTipoAccion(e.target.value)}>
                {objetosOptionsFull.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* SECCIÓN 2 — Fechas Clave */}
        <section className="cqp-section">
          <h2 className="cqp-section-h">2. Fechas Clave</h2>
          <div className="cqp-grid">
            <div className="cqp-field">
              <label>Fecha de Ingreso</label>
              <ScrollDateInput style={{ width: "100%" }} value={fi} onChange={setFi} />
            </div>
            <div className="cqp-field">
              <label>Fecha de Egreso / Despido</label>
              <ScrollDateInput style={{ width: "100%" }} value={fe} onChange={setFe} />
            </div>
          </div>
        </section>

        {/* SECCIÓN 3 — Salario y Zona */}
        <section className="cqp-section">
          <h2 className="cqp-section-h">3. Salario y Zona</h2>
          <div className="cqp-grid">
            <div className="cqp-field">
              <label>Zona Geográfica (SMG)</label>
              <select value={zona} onChange={(e) => setZona(e.target.value)}>
                <option value="general">Resto del País (General)</option>
                <option value="frontera">Frontera Norte</option>
              </select>
            </div>
            <div className="cqp-field">
              <label>Salario Diario Reconocido (MXN)</label>
              <div className="cqp-input-icon">
                <span>$</span>
                <input type="number" step="0.01" min="0" placeholder="0.00"
                  value={salManual} onChange={(e) => setSalManual(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="cqp-toggle-row" style={{ marginTop: "1.25rem" }}>
            <label className="cqp-toggle">
              <input type="checkbox" checked={sdiManualOn}
                onChange={(e) => { setSdiManualOn(e.target.checked); if (!e.target.checked) setSdiManualVal(""); }} />
              <span className="cqp-slider" />
            </label>
            <span className="cqp-toggle-label">Ingresar Salario Diario Integrado (SDI) Reconocido manualmente</span>
          </div>

          {sdiManualOn && (
            <div className="cqp-field cqp-full" style={{ marginTop: "1rem" }}>
              <label>SDI Reconocido Manual (MXN)</label>
              <div className="cqp-input-icon">
                <span>$</span>
                <input type="number" step="0.01" min="0" placeholder="0.00"
                  value={sdiManualVal} onChange={(e) => setSdiManualVal(e.target.value)} />
              </div>
            </div>
          )}

          <div className="cqp-toggle-row" style={{ marginTop: "1rem" }}>
            <label className="cqp-toggle">
              <input type="checkbox" checked={calcAltOn}
                onChange={(e) => { setCalcAltOn(e.target.checked); if (!e.target.checked) { setSalReclamado(""); setSdiReclamado(""); } }} />
              <span className="cqp-slider" />
            </label>
            <span className="cqp-toggle-label cqp-toggle-accent">Habilitar Cálculo Alternativo (Salario Reclamado)</span>
          </div>

          {calcAltOn && (
            <div className="cqp-grid cqp-alt-box">
              <div className="cqp-field">
                <label>Salario Diario Reclamado (MXN)</label>
                <div className="cqp-input-icon">
                  <span>$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00"
                    value={salReclamado} onChange={(e) => setSalReclamado(e.target.value)} />
                </div>
              </div>
              <div className="cqp-field">
                <label>SDI Reclamado Manual <span className="cqp-muted">(Opcional)</span></label>
                <div className="cqp-input-icon">
                  <span>$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00"
                    value={sdiReclamado} onChange={(e) => setSdiReclamado(e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </section>

        {/* SECCIÓN 4 — Horas Extras */}
        <section className="cqp-section">
          <h2 className="cqp-section-h">4. Horas Extras</h2>
          <div className="cqp-grid">
            <div className="cqp-field">
              <label>Tipo de Jornada</label>
              <select value={jornadaSel} onChange={(e) => setJornadaSel(e.target.value)}>
                <option value="8">Diurna (8 hrs)</option>
                <option value="7.5">Mixta (7.5 hrs)</option>
                <option value="7">Nocturna (7 hrs)</option>
              </select>
            </div>
            <div className="cqp-field">
              <label>Horas Extras a la Semana</label>
              <input type="number" min="0" placeholder="0"
                value={horasSemana} onChange={(e) => setHorasSemana(e.target.value)} />
            </div>
            <div className="cqp-field cqp-full">
              <label>Semanas Reclamadas</label>
              <input type="number" min="0" placeholder="0"
                value={semanasRec} onChange={(e) => setSemanasRec(e.target.value)} />
            </div>
          </div>
        </section>

        {/* NOTA INFORMATIVA */}
        <section className="cqp-info">
          <p>Las vacaciones cambian dependiendo el año de egreso (antes o después de Vacaciones Dignas 2023). La Prima de Antigüedad considera el tope histórico de 2 SMG de la zona seleccionada.</p>
        </section>

      </main>

      {/* ══════════════ RIGHT: RECIBO ══════════════ */}
      <aside className="cqp-right">
        <div className="cqp-receipt" ref={receiptRef}>

          {/* MEMBRETE */}
          <div className="cqp-membrete">
            <div className="cqp-mbr-title">
              <h1>ONTIVEROS &amp; ASOCIADOS</h1>
              <h2>ABOGADOS</h2>
            </div>
            <div className="cqp-mbr-sep" />
            <div className="cqp-mbr-contact">
              <div className="cqp-mbr-col">
                <p>Calle "H" y Ave. Marmoleros # 1598 Altos</p>
                <p>Tel. 686 5 54 48 52.</p>
                <p>Col. Industrial, Mexicali Baja California C.P. 21010</p>
                <p>Correo Electrónico: <u>recepcionmxl@ontiverosyasociados.com.mx</u></p>
              </div>
              <div className="cqp-mbr-col">
                <p>Calle 2 número 200 local 8, Plaza Gabriela, Comercial 83449</p>
                <p>Tels. 653 536-72-02, 653-515-19-90</p>
                <p>San Luis Río Colorado, Sonora</p>
                <p>Correo Electrónico: <u>victor@ontiverosyasociados.com.mx</u></p>
              </div>
            </div>
          </div>

          {/* HEADER DEL RECIBO */}
          <div className="cqp-rh">
            <h2 className="cqp-rh-title">Cuantificación</h2>
            <div className="cqp-rh-names">
              <div>Actor: <strong>{nombreTrabajador.trim() || "-"}</strong></div>
              <div>Demandado: <strong>{nombreEmpresa.trim() || "-"}</strong></div>
            </div>
            <div className="cqp-rh-meta">
              <span>Fecha: {fmtHoy()}</span>
              <span>
                {calc
                  ? `${calc.aniosE} años, ${calc.mesesE} meses, ${calc.diasE} días`
                  : "0 años, 0 meses, 0 días"}
              </span>
              <div className="cqp-badges">
                <span className="cqp-badge">SDI: {calc ? fmt(calc.sdi) : "$0.00"}</span>
                {calcAltOn && calcAlt && (
                  <span className="cqp-badge cqp-badge-alt">SDI Rec: {fmt(calcAlt.sdi)}</span>
                )}
              </div>
            </div>
          </div>

          {/* CABECERA DE COLUMNAS */}
          {calcAltOn && (
            <div className="cqp-cols-hdr">
              <div>Reconocido</div>
              <div>Reclamado</div>
            </div>
          )}

          {/* CUERPO */}
          <div className="cqp-rbody">

            {/* INDEMNIZACIONES */}
            {mostrarIndemnizaciones && (
              <div className="cqp-rsection">
                <h3>Indemnizaciones</h3>

                {procede90 && (
                  <div className="cqp-rrow">
                    <div className="cqp-rlabel">
                      <span>Indemnización Constitucional</span>
                      <small>90 días de SDI</small>
                    </div>
                    <div className="cqp-rvals">
                      <span className="cqp-val-main">{fmt(calc?.montoInd ?? 0)}</span>
                      {calcAltOn && <span className="cqp-val-alt">{fmt(calcAlt?.montoInd ?? 0)}</span>}
                    </div>
                  </div>
                )}

                {procedePrima && (
                  <div className="cqp-rrow">
                    <div className="cqp-rlabel">
                      <span>Prima de Antigüedad</span>
                      <small>
                        {calc
                          ? `${calc.prima.dias.toFixed(2)} días x ${fmt(calc.prima.salarioAplicado)}${calc.prima.topado ? " (Topado a 2 SMG)" : ""}`
                          : "12 días por año"}
                      </small>
                    </div>
                    <div className="cqp-rvals">
                      <span className="cqp-val-main">{fmt(calc?.prima.monto ?? 0)}</span>
                      {calcAltOn && <span className="cqp-val-alt">{fmt(calcAlt?.prima.monto ?? 0)}</span>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PRESTACIONES (FINIQUITO) */}
            <div className="cqp-rsection">
              <h3>Prestaciones (Finiquito)</h3>

              <div className="cqp-rrow">
                <div className="cqp-rlabel">
                  <span>Aguinaldo Proporcional</span>
                  <small>{calc ? `${calc.aguinaldo.dias.toFixed(2)} días proporcionales` : "0 días"}</small>
                </div>
                <div className="cqp-rvals">
                  <span className="cqp-val-main">{fmt(calc?.aguinaldo.monto ?? 0)}</span>
                  {calcAltOn && <span className="cqp-val-alt">{fmt(calcAlt?.aguinaldo.monto ?? 0)}</span>}
                </div>
              </div>

              <div className="cqp-rrow">
                <div className="cqp-rlabel">
                  <span>Vacaciones Proporcionales</span>
                  <small>{calc ? `${calc.vacas.diasTotales.toFixed(2)} días proporcionales` : "0 días"}</small>
                </div>
                <div className="cqp-rvals">
                  <span className="cqp-val-main">{fmt(calc?.vacas.montoVac ?? 0)}</span>
                  {calcAltOn && <span className="cqp-val-alt">{fmt(calcAlt?.vacas.montoVac ?? 0)}</span>}
                </div>
              </div>

              <div className="cqp-rrow">
                <div className="cqp-rlabel">
                  <span>Prima Vacacional</span>
                  <small>25% s/ Vacaciones</small>
                </div>
                <div className="cqp-rvals">
                  <span className="cqp-val-main">{fmt(calc?.vacas.montoPrima ?? 0)}</span>
                  {calcAltOn && <span className="cqp-val-alt">{fmt(calcAlt?.vacas.montoPrima ?? 0)}</span>}
                </div>
              </div>

              {mostrarHE && (
                <div className="cqp-rrow">
                  <div className="cqp-rlabel">
                    <span>Horas Extras</span>
                    <small>
                      {calc
                        ? `${calc.horasD} dobles, ${calc.horasT} triples x ${calc.semRec} sem.`
                        : "0 dobles, 0 triples"}
                    </small>
                  </div>
                  <div className="cqp-rvals">
                    <span className="cqp-val-main">{fmt(calc?.montoHE ?? 0)}</span>
                    {calcAltOn && <span className="cqp-val-alt">{fmt(calcAlt?.montoHE ?? 0)}</span>}
                  </div>
                </div>
              )}
            </div>

            {/* ESCENARIOS DE NEGOCIACIÓN */}
            {procedeNeg && calc && (
              <div className="cqp-rsection">
                <h3>Escenarios de Negociación</h3>
                {(() => {
                  const finiq = calc.aguinaldo.monto + calc.vacas.montoVac + calc.vacas.montoPrima;
                  const prim  = calc.prima.monto;
                  const sdi   = calc.sdi;
                  return [
                    { label: "Finiquito",              val: finiq },
                    { label: "+ Prima de Antigüedad",  val: finiq + prim },
                    { label: "+ 15 días",              val: finiq + prim + sdi * 15 },
                    { label: "+ 30 días",              val: finiq + prim + sdi * 30 },
                    { label: "+ 45 días",              val: finiq + prim + sdi * 45 },
                    { label: "+ 60 días",              val: finiq + prim + sdi * 60 },
                    { label: "+ 75 días",              val: finiq + prim + sdi * 75 },
                    { label: "+ 90 días",              val: finiq + prim + sdi * 90 },
                  ].map((r, i) => (
                    <div className="cqp-rrow" key={i}>
                      <div className="cqp-rlabel"><span>{r.label}</span></div>
                      <div className="cqp-rvals"><span className="cqp-val-main">{fmt(r.val)}</span></div>
                    </div>
                  ));
                })()}
              </div>
            )}

          </div>

          {/* FOOTER DEL RECIBO */}
          <div className="cqp-rfooter">
            <div className="cqp-total-row">
              <span>Total Proyectado</span>
              <div className="cqp-total-vals">
                <span className="cqp-grand">{fmt(calc?.total ?? 0)}</span>
                {calcAltOn && calcAlt && (
                  <span className="cqp-alt-grand">{fmt(calcAlt?.total ?? 0)}</span>
                )}
              </div>
            </div>
            <button className="cqp-btn-print" type="button" onClick={handleGuardarPDF} disabled={guardandoPDF}>
              {guardandoPDF ? "Generando PDF..." : "Guardar PDF"}
            </button>
          </div>

        </div>
      </aside>

    </div>
  );
}
